"use client";

import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

interface ToastProps {
  show: boolean;
  onComplete: () => void;
  type?: "welcome" | "calling" | "custom";
  title?: string;
  subtitle?: string;
  icon?: string;
  duration?: number;
  position?: "top" | "center" | "bottom";
  variant?: "primary" | "secondary" | "success" | "warning" | "error";
  customStyles?: string;
}

export function Toast({
  show,
  onComplete,
  type = "welcome",
  title,
  subtitle,
  icon,
  duration = 2500,
  position = "top",
  variant = "primary",
  customStyles,
}: ToastProps) {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isEntering, setIsEntering] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  // Default content based on type
  const getDefaultContent = () => {
    switch (type) {
      case "welcome":
        return {
          icon: "🎉",
          title: t("toast.welcome_back"),
          subtitle: "",
          variant: "primary" as const,
        };
      case "calling":
        return {
          icon: "📞",
          title: t("toast.calling"),
          subtitle: "050 XXX XXXX",
          variant: "secondary" as const,
        };
      default:
        return {
          icon: icon || "ℹ️",
          title: title || t("toast.notification"),
          subtitle: subtitle || "",
          variant: variant,
        };
    }
  };

  const content = getDefaultContent();

  // Get variant styles
  const getVariantStyles = () => {
    switch (content.variant) {
      case "primary":
        return "bg-[#9600F1] text-white";
      case "secondary":
        return "bg-[#200044] text-white";
      case "success":
        return "bg-green-600 text-white";
      case "warning":
        return "bg-yellow-600 text-white";
      case "error":
        return "bg-red-600 text-white";
      default:
        return "bg-[#9600F1] text-white";
    }
  };

  // Get position styles
  const getPositionStyles = () => {
    switch (position) {
      case "top":
        return "top-[13px] left-1/2 transform -translate-x-1/2";
      case "center":
        return "top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2";
      case "bottom":
        return "bottom-[13px] left-1/2 transform -translate-x-1/2";
      default:
        return "top-[13px] left-1/2 transform -translate-x-1/2";
    }
  };

  useEffect(() => {
    if (show) {
      // Start entrance animation with a small delay
      setTimeout(() => {
        setIsVisible(true);
        setIsEntering(true);
      }, 100);

      // After entrance animation completes
      setTimeout(() => {
        setIsEntering(false);
        setIsAnimating(true);
      }, 500);

      // Start exit animation
      setTimeout(() => {
        setIsAnimating(false);
        setIsExiting(true);

        // After exit animation completes
        setTimeout(() => {
          setIsVisible(false);
          setIsExiting(false);
          onComplete();
        }, 500);
      }, duration);
    }
  }, [show, onComplete, duration]);

  if (!isVisible) return null;

  return (
    <div
      className={`fixed ${getPositionStyles()} z-[90] transition-all duration-700 ease-out ${
        isEntering
          ? "translate-y-0 opacity-100 scale-100"
          : isExiting
            ? "-translate-y-full opacity-0 scale-95"
            : isAnimating
              ? "translate-y-0 opacity-100 scale-100"
              : "-translate-y-full opacity-0 scale-90"
      }`}
      style={{
        transform: isEntering
          ? "translateY(0) scale(1)"
          : isExiting
            ? "translateY(-100%) scale(0.8)"
            : isAnimating
              ? "translateY(0) scale(1)"
              : "translateY(-100%) scale(0.8)",
        opacity: isEntering || isAnimating ? 1 : 0,
        transition: "all 0.7s cubic-bezier(0.68, -0.55, 0.265, 1.55)",
      }}
    >
      <div
        className={`rounded-full px-4 py-2 flex items-center space-x-3 ${getVariantStyles()} ${
          customStyles || ""
        } shadow-lg`}
      >
        <span className="text-xl">{content.icon}</span>
        <div className="flex flex-col">
          <span className="text-sm font-medium">{content.title}</span>
          {content.subtitle && (
            <span className="text-xs opacity-90">{content.subtitle}</span>
          )}
        </div>
      </div>
    </div>
  );
}
